import React, { useCallback, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";
import {
  DEFAULT_CARRIER_META,
  carrierMetaFor,
  formatMailboxDate,
  formatMailboxTime,
  visibleDeliveries,
} from "./mailboxUtils.js";

function DeliveryCard({ delivery, carrierMeta, showDetails }) {
  const carrierId = delivery.displayCarrier || delivery.confirmedCarrier || delivery.carrier || "unknown";
  const meta = carrierMetaFor(carrierId, carrierMeta);

  return (
    <article className="mailbox-delivery-card" style={{ borderColor: meta.color, background: meta.bg }}>
      <div className="mailbox-delivery-card-header">
        <span className="mailbox-carrier-badge" style={{ background: meta.color }}>
          {delivery.displayCarrierLabel || delivery.carrierLabel || meta.label}
        </span>
      </div>
      <p className="mailbox-delivery-time">
        Delivered at <strong>{formatMailboxTime(delivery.deliveredAt)}</strong> Eastern
      </p>
      {showDetails ? (
        <p className="mailbox-delivery-meta">
          {delivery.detectedVia || delivery.cameraRole || "camera"}
          {delivery.deviceName ? ` · ${delivery.deviceName}` : ""}
        </p>
      ) : null}
    </article>
  );
}

function MailboxPage({ user, isAdmin, isMobile }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const carrierMeta = status?.carrierMeta || DEFAULT_CARRIER_META;
  const todayDeliveries = visibleDeliveries(status?.today?.deliveries);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const getMailboxStatus = httpsCallable(functions, "getMailboxStatus");
      const getMailboxHistory = httpsCallable(functions, "getMailboxHistory");
      const [statusRes, historyRes] = await Promise.all([
        getMailboxStatus(),
        getMailboxHistory({ days: 14 }),
      ]);
      setStatus(statusRes.data || null);
      setHistory(historyRes.data?.history || []);
    } catch (err) {
      console.error("Mailbox load error:", err);
      toast.error(err.message || "Failed to load mail delivery status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  return (
    <div className={`page-container mailbox-page ${isMobile ? "page-container-mobile" : ""}`}>
      <header className="mailbox-page-header">
        <h1 className="page-title">Mail Delivery</h1>
        <p className="page-subtitle">
          When mail and packages were delivered to the community mailbox area.
        </p>
      </header>

      {loading ? (
        <p className="mailbox-loading">Loading delivery status…</p>
      ) : (
        <>
          <section className="mailbox-today-panel">
            <div className="mailbox-today-panel-head">
              <h2>Today</h2>
              <span className="mailbox-window-pill">
                Monitoring {status?.deliveryWindow?.startHour ?? 6}:00 – {status?.deliveryWindow?.endHour ?? 18}:00 ET
              </span>
            </div>

            {todayDeliveries.length ? (
              <div className="mailbox-delivery-grid">
                {todayDeliveries.map((delivery) => (
                  <DeliveryCard
                    key={delivery.id || `${delivery.carrier}-${delivery.deliveredAt}`}
                    delivery={delivery}
                    carrierMeta={carrierMeta}
                    showDetails={isAdmin}
                  />
                ))}
              </div>
            ) : (
              <div className="mailbox-empty-today">
                <p className="mailbox-empty-title">No delivery detected yet today</p>
                <p className="mailbox-empty-text">Check back later — deliveries typically appear by early afternoon.</p>
              </div>
            )}
          </section>

          <section className="mailbox-history-panel">
            <h2>Recent deliveries</h2>
            <div className="mailbox-history-list">
              {history.map((day) => {
                const deliveries = visibleDeliveries(day.deliveries);
                return (
                  <div key={day.dateKey} className="mailbox-history-row">
                    <div className="mailbox-history-date">{formatMailboxDate(day.dateKey)}</div>
                    <div className="mailbox-history-deliveries">
                      {deliveries.length ? deliveries.map((d) => {
                        const meta = carrierMetaFor(d.displayCarrier || d.carrier, carrierMeta);
                        return (
                          <span
                            key={`${day.dateKey}-${d.id || d.carrier}-${d.deliveredAt}`}
                            className="mailbox-history-chip"
                            style={{ borderColor: meta.color, color: meta.color }}
                          >
                            {d.displayCarrierLabel || d.carrierLabel || meta.shortLabel} · {formatMailboxTime(d.deliveredAt)}
                          </span>
                        );
                      }) : (
                        <span className="mailbox-history-none">No delivery</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default MailboxPage;